import React from "react";
import { Plus, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useParlay, MAX_LEGS } from "@/lib/parlay";
import { useSportsbookLinks, trackAffiliateClick } from "@/lib/affiliateTracking";

const BETTING_ACCENT = "#067647";
const PASS_GREY = "#666666";
const BET_GREEN = "#067647";

// Helper: decimal → American odds string ("+250", "-130")
function decToAmerican(dec) {
  if (!dec || dec <= 1) return null;
  const v = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
  return v > 0 ? `+${v}` : `${v}`;
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#888] font-bold">
        {label}
      </span>
      <span className="font-display font-extrabold text-base md:text-lg text-[#111] leading-none">
        {value}
      </span>
      {sub && (
        <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#666] font-bold">
          {sub}
        </span>
      )}
    </div>
  );
}

export default function OddsStrip({ odds, index, game }) {
  const parlay = useParlay();
  const sportsbooks = useSportsbookLinks();
  if (!odds || !odds.matched) return null;
  const ml = odds.moneyline;
  const sp = odds.spread;
  const tot = odds.total;
  const edge = odds.edge_recommendation;
  const isBet = edge && edge.verdict === "BET";

  const legBase = game ? {
    sport: game.sport,
    home_team: game.home_team,
    away_team: game.away_team,
    date: game.date || null,
    time: game.time || null,
  } : null;
  const currentSide = legBase ? parlay.sideFor(legBase) : null;

  function pick(side) {
    if (!legBase) return;
    if (currentSide === side) {
      parlay.removeLeg({ ...legBase, side });
      toast(`Removed ${side === "home" ? game.home_team : game.away_team} from parlay slip.`);
      return;
    }
    const atCap = parlay.legs.length >= MAX_LEGS && !currentSide;
    if (atCap) {
      toast.error(`Parlay slip is full (max ${MAX_LEGS} legs).`);
      return;
    }
    parlay.addLeg({ ...legBase, side });
    parlay.setOpen(true);
    toast.success(
      `Added ${side === "home" ? game.home_team : game.away_team} ML to parlay.`,
      { description: `${parlay.legs.length + (currentSide ? 0 : 1)}/${MAX_LEGS} legs` }
    );
  }

  return (
    <div
      className="mb-4 border-2 border-[#111] p-3 md:p-4 bg-[#fafaf9]"
      data-testid={`odds-strip-${index}`}
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <span
          className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold inline-flex items-center gap-2"
          style={{ color: BETTING_ACCENT }}
        >
          Live odds · {odds.books_seen || ml?.books_count || 0} books
          {odds.model_active && (
            <span
              className="px-1.5 py-0.5 text-[9px] font-extrabold"
              style={{ background: BETTING_ACCENT, color: "#fff", letterSpacing: "0.15em" }}
              data-testid={`odds-model-active-${index}`}
              title="Proprietary ProbabilityModel output differs from market by ≥3pp — model is in the loop"
            >
              MODEL ACTIVE
            </span>
          )}
        </span>
        {edge && (
          <span
            className="font-mono text-[10px] tracking-[0.25em] uppercase font-extrabold px-2 py-1"
            data-testid={`odds-verdict-${index}`}
            style={{
              background: isBet ? BET_GREEN : "transparent",
              color: isBet ? "#fff" : PASS_GREY,
              border: `1.5px solid ${isBet ? BET_GREEN : PASS_GREY}`,
            }}
            title={`EdgeDetector confidence: ${edge.confidence || "n/a"}`}
          >
            {isBet
              ? `BET · ${(edge.edge * 100).toFixed(1)}% edge · ${(edge.confidence || "").toUpperCase()}`
              : "Pass — books efficient"}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {/* Moneyline */}
        {ml && (
          <Stat
            label="Moneyline"
            testid={`odds-ml-${index}`}
            value={
              <span className="inline-flex items-baseline gap-2">
                <span>{decToAmerican(ml.consensus.away_decimal)}</span>
                <span className="text-[#aaa] font-mono text-sm">/</span>
                <span>{decToAmerican(ml.consensus.home_decimal)}</span>
              </span>
            }
            sub={`${ml.away_team?.split(' ').pop()} / ${ml.home_team?.split(' ').pop()}`}
          />
        )}

        {/* Spread */}
        {sp && (
          <Stat
            label="Spread"
            testid={`odds-spread-${index}`}
            value={
              <span>
                {sp.consensus_home_point > 0 ? "+" : ""}
                {sp.consensus_home_point.toFixed(1)}
              </span>
            }
            sub={`${sp.home_team?.split(' ').pop()} line`}
          />
        )}

        {/* Total */}
        {tot && (
          <Stat
            label="Total"
            testid={`odds-total-${index}`}
            value={`O/U ${tot.consensus_total.toFixed(1)}`}
          />
        )}

        {/* Edge / Kelly */}
        {edge && (
          <Stat
            label={isBet ? "Best play" : "Verdict"}
            testid={`odds-edge-${index}`}
            value={
              isBet ? (
                <span style={{ color: BET_GREEN }}>
                  {edge.team?.split(' ').pop()} {decToAmerican(edge.best_decimal)}
                </span>
              ) : (
                <span style={{ color: PASS_GREY }}>No edge</span>
              )
            }
            sub={
              isBet
                ? `${edge.best_book} · Kelly ${(edge.kelly_pct_bankroll * 100).toFixed(2)}%`
                : "Try again as line moves"
            }
          />
        )}
      </div>

      {/* Best line / book per side — line shopping helper */}
      {ml && (
        <div
          className="mt-3 pt-3 border-t border-[#ddd] flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] tracking-[0.18em] uppercase text-[#555] font-bold"
          data-testid={`odds-best-books-${index}`}
        >
          <span>
            Best {ml.away_team?.split(' ').pop()}:{" "}
            <span className="text-[#111]">{decToAmerican(ml.best_away.decimal)}</span>
            <span className="text-[#888] ml-1.5 normal-case tracking-normal">
              @ {ml.best_away.book}
            </span>
          </span>
          <span>
            Best {ml.home_team?.split(' ').pop()}:{" "}
            <span className="text-[#111]">{decToAmerican(ml.best_home.decimal)}</span>
            <span className="text-[#888] ml-1.5 normal-case tracking-normal">
              @ {ml.best_home.book}
            </span>
          </span>
        </div>
      )}

      {/* Place at a sportsbook — CPA affiliate row. Only shown when there's
          a real moneyline to click through to. */}
      {ml && Object.keys(sportsbooks).length > 0 && (
        <div
          className="mt-3 pt-3 border-t border-[#ddd]"
          data-testid={`sportsbook-row-${index}`}
        >
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold mb-2">
            Place a bet
            <span className="normal-case tracking-normal text-[#888] font-normal ml-2">
              21+ · For entertainment only · 1-800-GAMBLER
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(sportsbooks).map(([partner, url]) => (
              <a
                key={partner}
                href={url}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={() => trackAffiliateClick({
                  partner,
                  kind: "sportsbook",
                  context: "odds-strip",
                  sport: game?.sport,
                  home_team: game?.home_team,
                  away_team: game?.away_team,
                })}
                data-testid={`sportsbook-${partner.toLowerCase().replace(/\s+/g, '-')}-${index}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase font-extrabold border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring active:translate-y-px no-underline"
                title={`Open this market on ${partner}`}
              >
                {partner}
                <ExternalLink size={10} strokeWidth={2.8} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Add to parlay */}
      {ml && legBase && (
        <div
          className="mt-3 pt-3 border-t border-[#ddd] flex flex-wrap items-center gap-2"
          data-testid={`parlay-add-row-${index}`}
        >
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold mr-1">
            + Parlay
          </span>
          {[
            { side: "away", team: ml.away_team, dec: ml.best_away.decimal },
            { side: "home", team: ml.home_team, dec: ml.best_home.decimal },
          ].map((opt) => {
            const active = currentSide === opt.side;
            return (
              <button
                key={opt.side}
                type="button"
                onClick={() => pick(opt.side)}
                data-testid={`parlay-add-${opt.side}-${index}`}
                aria-pressed={active}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] uppercase font-extrabold border-2 transition-colors focus-ring active:translate-y-px"
                style={{
                  background: active ? BETTING_ACCENT : "transparent",
                  color: active ? "#fff" : "#111",
                  borderColor: active ? BETTING_ACCENT : "#111",
                }}
                title={active ? "Remove from parlay slip" : "Add moneyline to parlay slip"}
              >
                {active ? <Check size={12} strokeWidth={3} /> : <Plus size={12} strokeWidth={3} />}
                {opt.team?.split(" ").pop()} {decToAmerican(opt.dec)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
